import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FileSignature, Plus, Search } from 'lucide-react';
import Toast from '../../components/Toast';
import Nr2jOwnerCell from '../../components/nr2j/Nr2jOwnerCell.jsx';
import Nr2jOwnerFilterSelect from '../../components/nr2j/Nr2jOwnerFilterSelect.jsx';
import { useToast } from '../../hooks/useToast';
import { useNr2jOwnerFilter } from '../../hooks/useNr2jOwnerFilter.js';
import apiService from '../../services/api';
import {
  NR2J_CONTRACT_STATUS_FILTERS,
  effectiveContractStatus,
  formatCentsAsEuros,
  formatNr2jDate,
  nr2jContractListBadge,
  organizationDisplayName,
} from '../../utils/nr2jConstants';

export default function Nr2jContractsList() {
  const navigate = useNavigate();
  const { toasts, toast, removeToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [q, setQ] = useState('');
  const [busyId, setBusyId] = useState(null);
  const ownerFilter = useNr2jOwnerFilter(items);
  const { params: ownerParams } = ownerFilter;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiService.listNr2jContracts({
        status: statusFilter || undefined,
        q: q.trim() || undefined,
        ...ownerParams,
      });
      setItems(res?.contracts || []);
    } catch (error) {
      toast.error(error?.message || 'Impossible de charger les contrats');
      // Conserver la liste précédente après erreur réseau / proxy.
    } finally {
      setLoading(false);
    }
  }, [statusFilter, q, ownerParams]);

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
  }, [load]);

  return (
    <div className="space-y-6" data-testid="nr2j-contracts-list">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Contrats</h1>
          <p className="text-sm text-gray-600">
            Création, envoi et signature des contrats commerciaux NR2J.
          </p>
        </div>
        <Link
          to="/nr2j/contrats/nouveau"
          className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-3 py-2 text-sm font-medium text-white"
        >
          <Plus className="h-4 w-4" /> Nouveau contrat
        </Link>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative max-w-md flex-1">
          <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher par numéro ou société…"
            className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm"
          />
        </div>
        <div className="flex flex-wrap gap-1">
          {NR2J_CONTRACT_STATUS_FILTERS.map((f) => (
            <button
              key={f.id || 'all'}
              type="button"
              onClick={() => setStatusFilter(f.id)}
              className={`rounded-full px-3 py-1 text-xs font-medium ${
                statusFilter === f.id
                  ? 'bg-indigo-600 text-white'
                  : 'border border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        {ownerFilter.showOwner ? (
          <Nr2jOwnerFilterSelect
            value={ownerFilter.value}
            onChange={ownerFilter.setValue}
            options={ownerFilter.options}
          />
        ) : null}
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        {loading ? (
          <p className="p-6 text-sm text-gray-500">Chargement…</p>
        ) : items.length === 0 ? (
          <div className="p-8 text-center">
            <FileSignature className="mx-auto h-8 w-8 text-gray-300" />
            <p className="mt-3 text-sm text-gray-600">Aucun contrat pour ce filtre.</p>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-100 text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
              <tr>
                <th className="px-4 py-3">Numéro</th>
                <th className="px-4 py-3">Version</th>
                <th className="px-4 py-3">Organisation</th>
                {ownerFilter.showOwner ? <th className="px-4 py-3">Commercial</th> : null}
                <th className="px-4 py-3">Montant TTC</th>
                <th className="px-4 py-3">Statut</th>
                <th className="px-4 py-3">Expiration</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {items.map((contract) => {
                const v = contract.currentVersion;
                const eff = v ? (v.effectiveStatus || effectiveContractStatus(v)) : null;
                const badge = nr2jContractListBadge(eff, contract.approvalStatus || contract.approval?.latest?.status);
                return (
                  <tr
                    key={contract.id}
                    className="cursor-pointer hover:bg-gray-50"
                    onClick={() => navigate(`/nr2j/contrats/${contract.id}`)}
                  >
                    <td className="px-4 py-3 font-medium text-gray-900">{contract.number}</td>
                    <td className="px-4 py-3 text-gray-600">
                      {v?.version != null ? `v${v.version}` : '—'}
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {organizationDisplayName(contract.organization)}
                    </td>
                    {ownerFilter.showOwner ? (
                      <td className="px-4 py-3">
                        <Nr2jOwnerCell
                          owner={contract.ownerUser || contract.organization?.ownerUser}
                          linkable={ownerFilter.canOpenMember}
                          viewerId={ownerFilter.viewerId}
                        />
                      </td>
                    ) : null}
                    <td className="px-4 py-3 text-gray-600">
                      {formatCentsAsEuros(v?.totalTtcCents)} €
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs ${badge.className}`}>
                        {badge.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {v?.expiresAt ? formatNr2jDate(v.expiresAt) : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap items-center gap-2" onClick={(e) => e.stopPropagation()}>
                        <Link
                          to={`/nr2j/contrats/${contract.id}`}
                          className="text-xs text-indigo-700 hover:underline"
                        >
                          Ouvrir
                        </Link>
                        {v?.hasPdf || v?.status === 'SENT' || v?.status === 'SIGNED' ? (
                          <button
                            type="button"
                            className="text-xs text-gray-600 hover:underline"
                            disabled={busyId === contract.id}
                            onClick={async () => {
                              setBusyId(contract.id);
                              try {
                                await apiService.downloadNr2jContractPdf(contract.id, {
                                  number: contract.number,
                                  version: v?.version,
                                });
                              } catch (error) {
                                toast.error(error?.message || 'PDF indisponible');
                              } finally {
                                setBusyId(null);
                              }
                            }}
                          >
                            PDF
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {toasts.map((item) => (
        <Toast key={item.id} toast={item} onClose={() => removeToast(item.id)} />
      ))}
    </div>
  );
}
